import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Permissions, useAuth } from '@kp/react-sdk';
import { useNavigate } from 'react-router';
import { useBreadcrumb } from '../../../contexts/breadcrumb-context';
import { entityBreadcrumbLabel } from '../../../utils/breadcrumb';
import { useUnitListQuery } from '../../../__generated__/types';
import * as Entities from '../../../constants/Entities';
import { UnitList } from './UnitList';
import { ErrorMessage } from '../../../components/Errors';
import { entityLocation } from '../../../utils/entity';
import { useFilters } from '../../../hooks/filters';
import { useHeader } from '../../../contexts/header-context';

export const UnitListContainer: React.FC = () => {
  const { t } = useTranslation(['units', 'general']);
  const navigate = useNavigate();
  const { setTitle } = useHeader();
  const { hasPermission } = useAuth();
  const { page, pageSize, query, setPage, setPageSize, setQuery } =
    useFilters();

  const handleAdd = () => navigate(entityLocation(Entities.UNIT, `new`));

  const { loading, error, data } = useUnitListQuery({
    variables: {
      take: pageSize,
      skip: page * pageSize,
      where: {
        name: { contains: query },
      },
    },
  });

  useBreadcrumb({
    title: entityBreadcrumbLabel(Entities.UNIT, t),
    location: Entities.UNIT.path,
  });

  const units = data?.units?.items ?? [];
  const count = data?.units?.totalCount ?? 0;

  useEffect(() => {
    setTitle({ main: t('units:list.title') });
  }, [setTitle, t]);

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <UnitList
      onSearchSubmit={setQuery}
      searchValue={query}
      loading={loading}
      page={page}
      pageSize={pageSize}
      count={count}
      onChangePageSize={setPageSize}
      onChangePage={setPage}
      units={units}
      onAdd={hasPermission(Permissions.UnitsWrite) ? handleAdd : undefined}
    />
  );
};
